import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiClient from "../api/api";
import Button from "./common/Button";
import FileUploadButton from "./common/FileUploadButton";
import useUploadQuestionsStore from "../store/uploadQuestionsStore";
import useQuestionBankStore from "../store/questionBankStore";
import { useColorConfig } from '../utils/colorConfig';

const UploadQuestionsDialog = ({ open, onClose }) => {
  const {
    excelFile,
    zipFile,
    setExcelFile,
    setZipFile,
    closeDialog,
    loading,
    setLoading,
  } = useUploadQuestionsStore();
  const navigate = useNavigate();
  const colors = useColorConfig();

  const handleClose = () => {
    closeDialog();
    if (onClose) onClose();
  };

  const handleSubmit = async () => {
    if (!excelFile) {
      toast.error("Please select an Excel file to upload.");
      return;
    }
    const fileType = excelFile.name.split(".").pop();
    if (fileType.toLowerCase() !== "xlsx") {
      toast.error("Only .xlsx files are allowed!");
      return;
    }

    if (zipFile) {
      const zipfileType = zipFile.name.split(".").pop();
      if (zipfileType.toLowerCase() !== "zip") {
        toast.error("Only .zip files are allowed!");
        return;
      }
    }

    const formData = new FormData();
    formData.append("file", excelFile);
    if (zipFile) {
      formData.append("images", zipFile);
    }

    setLoading(true);
    try {
      const response = await apiClient.post(
        `/rs/questionbankupload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        toast.success("File uploaded successfully!");
        closeDialog();
        try {
          useQuestionBankStore.getState().resetStore();
        } catch (err) {
          console.error("Failed to reset question bank store:", err);
        }
        navigate("/question-bank");
      } else {
        toast.error("Failed to upload the file. Please try again.");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred.";
      console.error("Error uploading file:", error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ m: 0, p: 2, display: "flex", justifyContent: "center" }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: "bold", color: colors.textPrimary }}>
          Upload Files
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderBottom: "none" }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <FileUploadButton
            title="Upload Excel File (.xlsx)"
            description="Drag & drop your .xlsx file here or click to select."
            acceptedFileTypes=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onFileUpload={setExcelFile}
            fileName={excelFile?.name}
            onRemoveFile={() => setExcelFile(null)}
            sx={{
              border: `2px dashed ${colors.primary || '#2196f3'}`,
              bgcolor: "transparent",
              "&:hover": {
                bgcolor: alpha(colors.primary || '#2196f3', 0.04),
                borderColor: colors.primary || '#1976d2',
              },
            }}
          />

          <FileUploadButton
            title="Upload Image ZIP File (.zip)"
            description="Drag & drop your .zip file here or click to select."
            acceptedFileTypes=".zip,application/zip,application/x-zip-compressed"
            onFileUpload={setZipFile}
            fileName={zipFile?.name}
            onRemoveFile={() => setZipFile(null)}
            sx={{
              border: `2px dashed ${colors.warning || '#ff9800'}`,
              bgcolor: "transparent",
              "&:hover": {
                bgcolor: alpha(colors.warning || '#ff9800', 0.04),
                borderColor: colors.warning || '#f57c00',
              },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{ p: 3, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            color="inherit"
            href="/assets/SampleMexaQ%26AFormat.xlsx"
            download="SampleMexaQ&AFormat.xlsx"
            sx={{ borderColor: colors.borderSubtle || "#ddd", color: colors.textPrimary || "#333", textTransform: "none" }}
          >
            Download Sample Excel
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            href="/assets/SampleImages.zip"
            download="SampleImages.zip"
            sx={{ borderColor: colors.borderSubtle || "#ddd", color: colors.textPrimary || "#333", textTransform: "none" }}
          >
            Download Sample .zip
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            sx={{ borderColor: colors.borderSubtle || "#ddd", color: colors.textPrimary || "#333", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default UploadQuestionsDialog;
